<template>
  <DialogLayout v-model="isOpen" :title="modalTitle" width="1200px">
    <AlertsAnimated :alerts="localAlerts" />

    <div class="pa-4 border-bottom">
      <v-row no-gutters class="mb-3">
        <v-col cols="7">
          <div class="task-details">
            <div class="mb-2 d-flex align-center">
              <span class="font-weight-medium mr-2">Task ID: </span>
              <span class="mr-3">{{ task.taskId }}</span>
            </div>
            <div class="mb-2">
              <span class="font-weight-medium">Due Date: </span>
              <span>{{ formatDateSlashUTC(task.dueAt) }}</span>
            </div>
            <div v-if="task && task.description" class="mb-2">
              <span class="font-weight-medium">Description: </span>
              <span>{{ task.description }}</span>
            </div>
            <div class="d-flex align-center">
              <span class="font-weight-medium mr-2">Status:</span>
              <v-chip
                v-if="isTaskDone"
                small
                color="success"
                text-color="white"
                label
              >
                <v-icon x-small left>mdi-check-circle</v-icon>
                Done
              </v-chip>
              <v-select
                v-else
                :value="task && task.resolution"
                :items="statusOptions"
                outlined
                dense
                hide-details
                style="max-width: 220px"
                :loading="statusUpdating"
                :disabled="statusUpdating"
                @change="onStatusChange"
              />
            </div>
          </div>
        </v-col>

        <v-col v-show="!isTaskDone" cols="5">
          <div class="qr-section">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="font-weight-medium">Mobile Access:</span>
              <v-btn
                x-small
                color="primary"
                outlined
                @click="showQRDialog = true"
              >
                <v-icon x-small left>mdi-qrcode</v-icon>
                Generate QR
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <div v-if="loadingDevicesOrUnits" class="pa-4">
      <v-skeleton-loader
        v-for="n in 5"
        :key="n"
        :loading="loadingDevicesOrUnits"
        type="card"
        height="70"
        class="mb-2"
      />
    </div>

    <!-- Assign step -->
    <div v-else-if="step === 'assign'" class="pa-4 method-c-assign">
      <div class="d-flex align-center mb-3">
        <span class="font-weight-bold subtitle-1">
          Unit Device Assignment
        </span>
        <v-spacer />
        <span class="text-caption grey--text">
          Target: {{ targetUnitsPerType }} device{{
            targetUnitsPerType === 1 ? "" : "s"
          }}
          per unit size/type ·
          {{ totalUnitAssignments }} units· {{ totalDeviceAssignments }} devices
        </span>
      </div>

      <!-- Completed type chips -->
      <div v-if="completedTypes.length" class="mb-4 d-flex flex-wrap">
        <v-chip
          v-for="ct in completedTypes"
          :key="ct.type"
          color="primary"
          outlined
          class="ma-1 method-c-chip"
          @click="openChipModal(ct.type)"
        >
          <v-icon x-small left>mdi-circle-small</v-icon>
          {{ ct.label }}
          <span class="method-c-chip-count ml-2">
            {{ ct.rows.length }} unit{{ ct.rows.length === 1 ? "" : "s" }} ·
            {{ chipDeviceCount(ct) }} devices
          </span>
        </v-chip>
      </div>

      <!-- Unit size/type dropdown -->
      <v-select
        :value="currentType"
        :items="unitTypeOptions"
        label="- Unit Size / Type -"
        outlined
        dense
        hide-details
        clearable
        class="method-c-type-select mb-4"
        @input="onSelectType"
      />

      <div
        v-if="!currentType"
        class="method-c-placeholder text--secondary font-italic py-4"
      >
        ← Select a Unit Size/Type to begin assigning.
      </div>

      <div v-else class="method-c-rows">
        <div class="method-c-progress mb-2">
          <span class="success--text">
            {{ currentTypeDeviceCount }} / {{ targetUnitsPerType }} devices
            assigned for this size/type
          </span>
        </div>

        <div
          v-for="(row, rowIdx) in rows"
          :key="`mc-row-${rowIdx}`"
          class="method-c-row d-flex align-center flex-wrap mb-2"
        >
          <div class="method-c-unit-confirmed mr-3">
            <span class="method-c-unit-label">Unit</span>
            <span class="method-c-unit-value">{{ row.unitName }}</span>
          </div>
          <div class="d-flex flex-wrap align-center">
            <v-chip
              v-for="(devName, devIdx) in row.deviceNames"
              :key="`mc-dev-${rowIdx}-${devIdx}`"
              color="success"
              close
              class="ma-1 method-c-device-tag"
              @click:close="removeDeviceFromRow(rowIdx, devIdx)"
            >
              {{ devName }}
            </v-chip>
          </div>
        </div>

        <!-- Active pending row -->
        <div class="method-c-row d-flex align-center flex-wrap mb-2">
          <v-select
            v-model="pending.unitId"
            :items="
              unitsForCurrentType.map((u) => ({ value: u.id, text: u.name }))
            "
            label="- select unit -"
            outlined
            dense
            hide-details
            class="method-c-unit mr-2"
            style="max-width: 200px"
          />
          <v-select
            v-for="(_, devIdx) in pending.deviceIds"
            :key="`mc-pending-dev-${devIdx}`"
            :value="pending.deviceIds[devIdx]"
            :items="
              devicesAvailableForSlot(devIdx).map((d) => ({
                value: d.id,
                text: d.name,
              }))
            "
            label="- select Device -"
            outlined
            dense
            hide-details
            clearable
            @input="(v) => setPendingDevice(devIdx, v)"
            class="mr-2"
            style="max-width: 200px"
          />
          <v-btn
            small
            rounded
            color="primary"
            :disabled="completing"
            @click="assignPendingRow"
          >
            Assign
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Summary step -->
    <div v-else-if="step === 'summary'" class="pa-4">
      <div class="d-flex align-center mb-3">
        <h3 class="method-c-summary-title">Assignment Summary</h3>
        <v-spacer />
        <v-btn
          v-if="!isTaskDone"
          icon
          small
          color="primary"
          title="Edit assignments"
          @click="backToAssign"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
      </div>

      <v-card outlined class="mb-4">
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Unit</th>
              <th>Size / Type</th>
              <th>Devices</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in summaryRows" :key="`mc-sum-${idx}`">
              <td class="font-weight-medium">{{ r.unitName }}</td>
              <td>{{ r.unitTypeLabel }}</td>
              <td>
                <v-chip
                  v-for="(devName, di) in r.deviceNames"
                  :key="`mc-sum-d-${idx}-${di}`"
                  x-small
                  color="success"
                  class="ma-1"
                >
                  {{ devName }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>

      <!-- Send Notification (only before the task is Done) -->
      <v-card v-if="!isTaskDone" outlined class="pa-3 method-c-notif-card">
        <div class="font-weight-bold mb-2">Send Notification</div>
        <v-text-field
          v-model="notifEmail"
          label="email address"
          type="email"
          outlined
          dense
          hide-details
          class="mb-2"
          :disabled="notifSending || notifSent"
        />
        <div class="d-flex align-center mb-2 text-caption text--secondary">
          <v-icon x-small left>mdi-account-circle</v-icon>
          Assigned by:&nbsp;<strong>{{ assignedByName || "—" }}</strong>
        </div>
        <v-btn
          block
          color="error"
          outlined
          :loading="notifSending"
          :disabled="notifSent"
          @click="sendNotification"
        >
          {{ notifSent ? "Notification sent ✓" : "Send Notification" }}
        </v-btn>
      </v-card>
    </div>

    <!-- Chip detail modal -->
    <v-dialog :value="chipModal.open" max-width="520" @input="closeChipModal">
      <v-card v-if="chipModalType">
        <v-card-title class="grey lighten-3 py-2 subtitle-1">
          {{ chipModalType.label }} — Assigned Details
          <v-spacer />
          <v-btn icon small @click="closeChipModal">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Unit</th>
                <th>Device</th>
                <th class="text-center">Remove</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(r, ri) in chipModalType.rows">
                <tr
                  v-for="(devName, di) in r.deviceNames"
                  :key="`chip-r-${ri}-d-${di}`"
                >
                  <td>{{ r.unitName }}</td>
                  <td>{{ devName }}</td>
                  <td class="text-center">
                    <v-btn
                      icon
                      x-small
                      color="error"
                      @click="removeChipDevice(chipModalType.type, ri, di)"
                    >
                      <v-icon x-small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </tbody>
          </v-simple-table>
          <v-btn
            block
            color="error"
            class="mt-4"
            @click="removeAllChipEntries(chipModalType.type)"
          >
            Remove All Entries
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <template #actions>
      <!-- Action buttons hidden once the task is Done — read-only view. -->
      <template v-if="!isTaskDone && step === 'assign'">
        <v-btn
          text
          color="primary"
          :disabled="!currentTypeMeetsTarget"
          @click="selectNext"
        >
          Select Next
        </v-btn>
        <v-btn
          color="success"
          depressed
          :disabled="!canComplete"
          @click="goToSummary"
        >
          Complete Task
        </v-btn>
      </template>

      <template v-if="!isTaskDone && step === 'summary'">
        <v-btn text @click="backToAssign">Back</v-btn>
        <v-btn
          color="success"
          depressed
          large
          :loading="completing"
          :disabled="completedTypes.length === 0"
          @click="finishTask"
        >
          FINISH
        </v-btn>
      </template>
    </template>

    <QRCodeDialog
      v-model="showQRDialog"
      title="Mobile Task Access"
      description="Scan this QR code with your mobile phone or tablet to manage this pre-install task."
      :url-generator="generatePreInstallUrl"
    />
  </DialogLayout>
</template>

<script>
import DialogLayout from "@layout/DialogLayout.vue";
import QRCodeDialog from "../mobile/QRCodeDialog.vue";
import { UPDATE_TASK } from "./graphql";
import { RESOLUTION_STATUS } from "@tod-ui/constants/task_management";
import { SEND_PREINSTALLED_DEVICE_MOBILE_SUMMARY } from "@components/smart_units/graphql";
import { PREINSTALLED_INVENTORY_UNITS } from "@components/smart_units/graphql";
import { formatDateSlashUTC } from "@tod-ui/helpers/datetime";
import useUser from "@components/authentication/useUser";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import useInventory from "@components/smart_units/useInventory";
import useDevice from "@components/smart_units/useDevice";
import { titleCase } from "@tod-ui/helpers/strings";

export default {
  name: "DialogPreInstallMethodC",
  components: { DialogLayout, AlertsAnimated, QRCodeDialog },
  props: {
    value: { type: Boolean, default: false },
    task: { type: Object, required: false, default: null },
  },
  setup() {
    const { defaultOrganization, currentUser } = useUser();
    const { addLocalAlert, clearAllAlerts, localAlerts } = useAlerts();
    const {
      officeInventory,
      officeInventoryLoading,
      fetchTabData,
      formatDeviceName,
    } = useInventory();

    return {
      defaultOrganization,
      currentUser,
      addLocalAlert,
      clearAllAlerts,
      localAlerts,
      officeInventory,
      officeInventoryLoading,
      fetchTabData,
      formatDeviceName,
    };
  },
  data() {
    return {
      completing: false,
      showQRDialog: false,
      vacantUnits: [],
      vacantUnitsLoading: false,
      step: "assign", // "assign" | "summary"
      currentType: null,
      rows: [],
      pending: { unitId: null, deviceIds: [] },
      completedTypes: [],
      chipModal: { open: false, type: null },
      // Send Notification state (shown on summary page before FINISH)
      // Note: "assigned by" comes from the logged-in user (assignedByName
      // computed) — no separate input field anymore.
      notifEmail: "",
      notifSending: false,
      notifSent: false,
      // Status selector — same pattern as DialogViewTask. Lets the user
      // change resolution (e.g. cancel) without going through the FINISH
      // assignment flow.
      statusUpdating: false,
      statusOptions: [
        { text: "To Do", value: RESOLUTION_STATUS.TODO },
        { text: "Pending", value: RESOLUTION_STATUS.PENDING },
        { text: "In Progress", value: RESOLUTION_STATUS.IN_PROGRESS },
        { text: "Cancelled", value: RESOLUTION_STATUS.CANCELLED },
      ],
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
        if (!v) {
          this.$emit("close");
          this.clearAllAlerts();
          this.resetState();
        }
      },
    },
    modalTitle() {
      return (this.task && this.task.title) || "Pre-Install Devices";
    },
    isTaskDone() {
      return this.task && this.task.resolution === "done";
    },
    loadingDevicesOrUnits() {
      return this.officeInventoryLoading || this.vacantUnitsLoading;
    },
    availableDevices() {
      const devices = this.officeInventory || [];
      return devices
        .filter(
          (device) =>
            device.type === "device" && device.devices && device.devices[0]
        )
        .map((device) => {
          const d = device.devices[0];
          return {
            // Spread MUST come first — if the raw device record has
            // `name: null`, putting the spread last would overwrite the
            // explicit fallback below and the dropdown would show "null".
            ...d,
            id: d.id,
            name:
              this.formatDeviceName(d) ||
              d.shortId ||
              d.externalId ||
              `Device ${d.id}`,
            shortId: d.shortId || "",
            externalId: d.externalId || "",
            status: d.status || "unknown",
          };
        });
    },
    availableUnits() {
      return this.vacantUnits;
    },
    // Per PRD: target_count is the number of UNITS the technician must
    // assign per unit size+type combination. Each unit gets exactly one
    // device. (Earlier interpretation — N devices per unit — was incorrect.)
    targetUnitsPerType() {
      const serverTarget =
        this.task && this.task.actions && this.task.actions.targetCount;
      if (serverTarget && serverTarget > 0) return serverTarget;
      const c =
        this.task && this.task.taskRule && this.task.taskRule.conditions;
      return (c && c.devicesPerUnit) || 1;
    },
    // Always 1 — Method C is now "1 device per unit, target N units per
    // size/type". Kept as a name in case future variants need >1.
    devicesPerUnitRow() {
      return 1;
    },
    // [{ key, sizeLabel, typeName, vacantCount }] grouped by SIZE × TYPE
    // (not just type). Key = "WxL|TypeName" so re-selection re-hydrates the
    // correct chip. Hidden: parking units (per existing exclusion).
    unitTypeOptions() {
      const groups = new Map();
      (this.availableUnits || []).forEach((u) => {
        const ut = u.unitType || {};
        const typeName = ut.name || "Unknown";
        if (typeName.toLowerCase().includes("parking")) return;
        const sizeLabel = this.unitSizeLabel(ut);
        const key = `${sizeLabel}|${typeName}`;
        const existing = groups.get(key);
        if (existing) {
          existing.vacantCount += 1;
        } else {
          groups.set(key, {
            key,
            sizeLabel,
            typeName,
            vacantCount: 1,
          });
        }
      });
      return Array.from(groups.values())
        .sort((a, b) => a.sizeLabel.localeCompare(b.sizeLabel))
        .map((g) => ({
          value: g.key,
          text: `${g.sizeLabel} (${this.titleCase(g.typeName)}) — ${
            g.vacantCount
          } vacant`,
        }));
    },
    unitsForCurrentType() {
      if (!this.currentType) return [];
      const lockedUnitIds = new Set(
        this.completedTypes
          .flatMap((ct) => ct.rows.map((r) => r.unitId))
          .filter(Boolean)
      );
      return (this.availableUnits || []).filter((u) => {
        const ut = u.unitType || {};
        const typeName = ut.name || "Unknown";
        if (typeName.toLowerCase().includes("parking")) return false;
        const key = `${this.unitSizeLabel(ut)}|${typeName}`;
        if (key !== this.currentType) return false;
        return !lockedUnitIds.has(u.id);
      });
    },
    // Per-slot device list. Excludes devices already locked into confirmed
    // rows or completed type chips, AND devices picked by *other* pending
    // slots — but keeps THIS slot's current pick included so the v-select
    // can resolve its value to a label (otherwise it shows blank after
    // selection because the value disappears from `:items`).
    devicesAvailableForSlot() {
      return (slotIdx) => {
        const used = new Set();
        this.rows.forEach((r) =>
          (r.deviceIds || []).forEach((id) => id && used.add(id))
        );
        this.completedTypes.forEach((ct) =>
          ct.rows.forEach((r) =>
            (r.deviceIds || []).forEach((id) => id && used.add(id))
          )
        );
        (this.pending.deviceIds || []).forEach((id, i) => {
          if (id && i !== slotIdx) used.add(id);
        });
        return (this.availableDevices || []).filter((d) => !used.has(d.id));
      };
    },
    totalUnitAssignments() {
      return (
        this.completedTypes.reduce((sum, ct) => sum + ct.rows.length, 0) +
        this.rows.length
      );
    },
    totalDeviceAssignments() {
      const count = (rows) =>
        rows.reduce(
          (sum, r) => sum + (r.deviceIds || []).filter(Boolean).length,
          0
        );
      return (
        this.completedTypes.reduce((sum, ct) => sum + count(ct.rows), 0) +
        count(this.rows)
      );
    },
    hasCurrentRows() {
      return this.rows.length > 0;
    },
    currentTypeDeviceCount() {
      return this.rows.reduce(
        (s, r) => s + (r.deviceIds || []).filter(Boolean).length,
        0
      );
    },
    currentTypeMeetsTarget() {
      return this.rows.length > 0;
    },
    canComplete() {
      const currentHasRows = this.currentType && this.hasCurrentRows;
      return this.completedTypes.length > 0 || currentHasRows;
    },
    summaryRows() {
      const out = [];
      this.completedTypes.forEach((ct) => {
        ct.rows.forEach((r) => {
          out.push({ unitType: ct.type, unitTypeLabel: ct.label, ...r });
        });
      });
      return out;
    },
    chipModalType() {
      return this.completedTypes.find((c) => c.type === this.chipModal.type);
    },

    // Always derived from the logged-in user — surfaced on the summary page
    // and sent as `doneBy` on the email mutation.
    assignedByName() {
      const u = this.currentUser;
      if (!u) return "";
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || u.username || "";
    },
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal && this.task) {
          this.resetState();
          this.clearAllAlerts();
          this.initialize();
        }
      },
      immediate: true,
    },
    task: {
      handler(newTask, oldTask) {
        if (!newTask || !this.value) return;
        const newId = newTask.id;
        const oldId = oldTask && oldTask.id;
        if (newId !== oldId) {
          this.resetState();
          this.initialize();
        }
      },
      deep: true,
    },
  },
  methods: {
    async initialize() {
      if (!this.task) return;
      const facilityId =
        this.task && this.task.organization && this.task.organization.id;
      if (!facilityId) return;

      try {
        // Office inventory (staged in_service devices, not yet assigned to a unit).
        await this.fetchTabData(
          0,
          this.$apollo.provider.defaultClient,
          100,
          "",
          facilityId
        );
        // Vacant units — query directly so we only see units that don't already
        // have a device installed.
        await this.loadVacantUnits(facilityId);
        await this.$nextTick();
      } catch (error) {
        console.error("Method C: failed to load inventory/units", error);
        this.addLocalAlert({
          type: "error",
          message: "Failed to load available devices or units.",
          timeout: 5,
        });
      }

      this.hydrateFromSavedAssignments();
      // If the task was already completed, jump straight to the summary view
      // so the user sees what was assigned (parity with Methods A/B).
      if (this.isTaskDone && this.completedTypes.length > 0) {
        this.step = "summary";
      }
      this.resetPending();
    },

    // Rebuilds completedTypes from the server-saved assignmentsByType payload
    // so viewing / re-opening a task shows previously assigned units+devices.
    hydrateFromSavedAssignments() {
      const saved =
        (this.task &&
          this.task.actions &&
          this.task.actions.assignmentsByType) ||
        [];
      if (!saved.length) return;

      const byType = {};
      saved.forEach((entry) => {
        const type = entry.unitType || entry.unit_type;
        if (!type) return;
        if (!byType[type]) byType[type] = [];
        byType[type].push({
          unitId: entry.unitId || entry.unit_id || null,
          unitName: entry.unitName || entry.unit_name || "",
          deviceIds: entry.deviceIds || entry.device_ids || [],
          deviceNames: entry.deviceNames || entry.device_names || [],
        });
      });

      this.completedTypes = Object.keys(byType).map((type) => ({
        type,
        label: this.chipLabel(type),
        rows: byType[type],
      }));
    },

    async loadVacantUnits(facilityId) {
      this.vacantUnitsLoading = true;
      try {
        const result = await this.$apollo.query({
          query: PREINSTALLED_INVENTORY_UNITS,
          variables: {
            id: facilityId,
            assigned: false,
            deviceAssigned: false,
            type: "zone",
            subtype: "unit",
            limit: 500,
            page: 1,
            sort: "name asc",
          },
          fetchPolicy: "network-only",
        });
        const data =
          result.data && result.data.organizationDescendantsPaginated;
        this.vacantUnits = (data && data.data) || [];
      } finally {
        this.vacantUnitsLoading = false;
      }
    },

    resetState() {
      this.step = "assign";
      this.currentType = null;
      this.rows = [];
      this.completedTypes = [];
      this.chipModal = { open: false, type: null };
      this.notifEmail = "";
      this.notifSending = false;
      this.notifSent = false;
      this.resetPending();
    },
    resetPending() {
      // Always 1 device slot per pending row — Method C now means
      // "1 device on each of N units per size/type".
      this.pending = {
        unitId: null,
        deviceIds: [null],
      };
    },

    // Vue 2 doesn't track `arr[i] = v` mutations by index, so we use $set to
    // ensure the v-select re-renders with the picked value.
    setPendingDevice(idx, value) {
      this.$set(this.pending.deviceIds, idx, value);
    },

    // Build a "WxL" size label from a unitType's width/length. Falls back to
    // pulling a size pattern out of the type name (e.g. "5x5 Climate" → "5x5")
    // when width/length aren't on the record.
    unitSizeLabel(unitType) {
      if (!unitType) return "Unknown";
      const w = unitType.width;
      const l = unitType.length;
      if (w && l) return `${w}X${l}`;
      const m =
        typeof unitType.name === "string"
          ? unitType.name.match(/(\d+)\s*[xX]\s*(\d+)/)
          : null;
      if (m) return `${m[1]}X${m[2]}`;
      return "Unsized";
    },

    // Composite chip key "5X5|Climate Control" → friendly label.
    // Falls back to titleCase for legacy keys without the "|".
    chipLabel(key) {
      if (!key) return "";
      const idx = key.indexOf("|");
      if (idx === -1) return this.titleCase(key);
      const size = key.slice(0, idx);
      const typeName = key.slice(idx + 1);
      return `${size} (${this.titleCase(typeName)})`;
    },

    onSelectType(type) {
      this.currentType = type || null;
      const existing = this.completedTypes.find((c) => c.type === type);
      if (existing) {
        this.rows = existing.rows.map((r) => ({
          unitId: r.unitId,
          unitName: r.unitName,
          deviceIds: [...(r.deviceIds || [])],
          deviceNames: [...(r.deviceNames || [])],
        }));
        this.completedTypes = this.completedTypes.filter(
          (c) => c.type !== type
        );
      } else {
        this.rows = [];
      }
      this.resetPending();
    },

    assignPendingRow() {
      const p = this.pending;
      if (!p.unitId) {
        this.addLocalAlert({
          type: "warning",
          message: "Please select a unit.",
          timeout: 5,
        });
        return;
      }
      const chosen = (p.deviceIds || []).filter(Boolean);
      if (chosen.length === 0) {
        this.addLocalAlert({
          type: "warning",
          message: "Please select at least one device.",
          timeout: 5,
        });
        return;
      }
      const unit = (this.availableUnits || []).find((u) => u.id === p.unitId);
      const unitName = (unit && unit.name) || `Unit ${p.unitId}`;
      const deviceNames = chosen.map((id) => {
        const d = (this.availableDevices || []).find((x) => x.id === id);
        return (d && d.name) || id;
      });
      const existingRowIdx = this.rows.findIndex((r) => r.unitId === p.unitId);
      if (existingRowIdx >= 0) {
        const existing = this.rows[existingRowIdx];
        this.$set(this.rows, existingRowIdx, {
          ...existing,
          deviceIds: [...existing.deviceIds, ...chosen],
          deviceNames: [...existing.deviceNames, ...deviceNames],
        });
      } else {
        this.rows.push({
          unitId: p.unitId,
          unitName,
          deviceIds: chosen,
          deviceNames,
        });
      }
      this.resetPending();
    },

    removeDeviceFromRow(rowIdx, deviceIdx) {
      const row = this.rows[rowIdx];
      if (!row) return;
      row.deviceIds.splice(deviceIdx, 1);
      row.deviceNames.splice(deviceIdx, 1);
      if (row.deviceIds.length === 0) this.rows.splice(rowIdx, 1);
    },

    selectNext() {
      if (!this.currentType || !this.hasCurrentRows) {
        this.addLocalAlert({
          type: "warning",
          message: "Please select a unit and assign at least one device first.",
          timeout: 5,
        });
        return;
      }
      this.saveCurrentTypeAsChip();
      this.currentType = null;
      this.rows = [];
      this.resetPending();
    },

    saveCurrentTypeAsChip() {
      if (!this.currentType || !this.hasCurrentRows) return;
      const chip = {
        type: this.currentType,
        label: this.chipLabel(this.currentType),
        rows: this.rows.map((r) => ({
          unitId: r.unitId,
          unitName: r.unitName,
          deviceIds: [...(r.deviceIds || [])],
          deviceNames: [...(r.deviceNames || [])],
        })),
      };
      const idx = this.completedTypes.findIndex((c) => c.type === chip.type);
      if (idx >= 0) this.completedTypes.splice(idx, 1, chip);
      else this.completedTypes.push(chip);
    },

    openChipModal(type) {
      this.chipModal = { open: true, type };
    },
    closeChipModal() {
      this.chipModal = { open: false, type: null };
    },
    removeChipDevice(type, rowIdx, deviceIdx) {
      const ct = this.completedTypes.find((c) => c.type === type);
      if (!ct) return;
      const row = ct.rows[rowIdx];
      if (!row) return;
      row.deviceIds.splice(deviceIdx, 1);
      row.deviceNames.splice(deviceIdx, 1);
      if (row.deviceIds.length === 0) ct.rows.splice(rowIdx, 1);
      if (ct.rows.length === 0) {
        this.completedTypes = this.completedTypes.filter(
          (c) => c.type !== type
        );
        this.closeChipModal();
      }
    },
    removeAllChipEntries(type) {
      if (!window.confirm("Remove all entries for this unit type?")) return;
      this.completedTypes = this.completedTypes.filter((c) => c.type !== type);
      this.closeChipModal();
    },

    chipDeviceCount(ct) {
      return ct.rows.reduce(
        (sum, r) => sum + (r.deviceIds || []).filter(Boolean).length,
        0
      );
    },

    goToSummary() {
      if (this.currentType && this.hasCurrentRows) {
        this.saveCurrentTypeAsChip();
        this.currentType = null;
        this.rows = [];
        this.resetPending();
      }
      if (this.completedTypes.length === 0) {
        this.addLocalAlert({
          type: "warning",
          message: "Please assign at least one unit before completing.",
          timeout: 5,
        });
        return;
      }
      this.step = "summary";
    },

    backToAssign() {
      this.step = "assign";
    },

    // Status change from the dialog header — mirrors DialogViewTask's
    // updateTask("task_status") flow. User can cancel a task or move it
    // to in-progress without going through the FINISH assignment flow.
    // We pass through the existing required UPDATE_TASK args (assignee,
    // org, type, etc.) so the resolver doesn't reject the mutation.
    async onStatusChange(newResolution) {
      if (!this.task || !newResolution) return;
      if (newResolution === this.task.resolution) return;

      this.statusUpdating = true;
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_TASK,
          variables: {
            id: this.task.id,
            assigneeId: this.task.assignee && this.task.assignee.id,
            description: this.task.description,
            dueAt: this.task.dueAt,
            organizationId: this.task.organization.id,
            priority: this.task.priority,
            resolution: newResolution,
            taskTypeId: this.task.taskType.id,
            title: this.task.title,
          },
        });

        this.$emit("task-updated", {
          type: "status_change",
          taskId: this.task.id,
          resolution: newResolution,
        });

        this.addLocalAlert({
          type: "success",
          message: `Task status changed to "${newResolution}".`,
        });

        // If user just cancelled, close the dialog so the next open task can
        // be picked up. Other status changes keep the dialog open. The
        // isOpen setter handles emit + state cleanup.
        if (newResolution === "cancelled") {
          this.isOpen = false;
        }
      } catch (error) {
        console.error("Method C: status change failed", error);
        const gql = error.graphQLErrors && error.graphQLErrors.length > 0;
        const msg = gql
          ? error.graphQLErrors[0].message
          : error.message || "Failed to update task status.";
        this.addLocalAlert({ type: "error", message: msg });
      } finally {
        this.statusUpdating = false;
      }
    },

    // Reuses the existing sendPreinstalledDevicesMobileSummary mutation (same
    // one Methods A/B use from the mobile flow) — expects a flat list of
    // {unit_name, device_name} pairs. We expand each Method C row to one entry
    // per device on the unit.
    async sendNotification() {
      const email = (this.notifEmail || "").trim();
      // "assigned by" comes from the logged-in user — never user-typed.
      const by = (this.assignedByName || "").trim() || "Unknown User";
      if (!email) {
        this.addLocalAlert({
          type: "warning",
          message: "Please enter an email address.",
          timeout: 5,
        });
        return;
      }
      if (this.completedTypes.length === 0) {
        this.addLocalAlert({
          type: "warning",
          message: "No assignments to send.",
          timeout: 5,
        });
        return;
      }

      this.notifSending = true;
      this.clearAllAlerts();

      const devicesDetails = [];
      this.completedTypes.forEach((ct) =>
        ct.rows.forEach((r) =>
          (r.deviceNames || []).forEach((devName) => {
            if (devName) {
              devicesDetails.push({
                unitName: r.unitName,
                deviceName: devName,
              });
            }
          })
        )
      );

      try {
        await this.$apollo.mutate({
          mutation: SEND_PREINSTALLED_DEVICE_MOBILE_SUMMARY,
          variables: {
            facilityId: this.task.organization.id,
            doneBy: by,
            emails: [email],
            devicesDetails,
          },
        });
        this.notifSent = true;
        this.addLocalAlert({
          type: "success",
          message: `Notification sent to ${email}.`,
          timeout: 5,
        });
      } catch (error) {
        console.error("Method C: sendNotification failed", error);
        const gql = error.graphQLErrors && error.graphQLErrors.length > 0;
        if (gql) {
          error.graphQLErrors.forEach((e) =>
            this.addLocalAlert({
              type: "error",
              message: e.message,
              timeout: 5,
            })
          );
        } else {
          this.addLocalAlert({
            type: "error",
            message: error.message || "Failed to send notification.",
            timeout: 5,
          });
        }
      } finally {
        this.notifSending = false;
      }
    },

    async finishTask() {
      if (!this.task || this.completedTypes.length === 0) return;
      this.clearAllAlerts();
      this.completing = true;

      try {
        // 1. Move each device to its assigned unit.
        const moves = [];
        this.completedTypes.forEach((ct) =>
          ct.rows.forEach((r) =>
            (r.deviceIds || []).forEach((did) => {
              const device = (this.availableDevices || []).find(
                (d) => d.id === did
              );
              if (device) moves.push({ device, unitId: r.unitId });
            })
          )
        );
        await Promise.all(
          moves.map(({ device, unitId }) => {
            const { moveDevice } = useDevice(device);
            return moveDevice(this.$apollo, unitId, null);
          })
        );

        // 2. Build assignmentsByType payload.
        const payload = [];
        this.completedTypes.forEach((ct) =>
          ct.rows.forEach((r) =>
            payload.push({
              unitType: ct.type,
              unitId: r.unitId,
              unitName: r.unitName,
              deviceIds: [...(r.deviceIds || [])],
              deviceNames: [...(r.deviceNames || [])],
            })
          )
        );

        // 3. Preserve existing checklist.
        const existingChecklist =
          this.task.actions && Array.isArray(this.task.actions.checklist)
            ? this.task.actions.checklist.map((item) => ({
                id: item.id,
                completed: item.completed || false,
                comment: item.comment || "",
                description: item.description || "",
              }))
            : [];

        await this.$apollo.mutate({
          mutation: UPDATE_TASK,
          variables: {
            id: this.task.id,
            assigneeId: this.task.assignee.id,
            description: this.task.description,
            dueAt: this.task.dueAt,
            organizationId: this.task.organization.id,
            priority: this.task.priority,
            resolution: "done",
            taskTypeId: this.task.taskType.id,
            title: this.task.title,
            actions: {
              checklist: existingChecklist,
              assignmentsByType: payload,
            },
          },
        });

        this.$emit("task-updated", {
          type: "complete_preinstall_task",
          taskId: this.task.id,
          assignedCount: payload.length,
          resolution: "done",
        });

        this.isOpen = false;
      } catch (error) {
        console.error("Method C: finishTask failed", error);
        const gqlErrs = error.graphQLErrors && error.graphQLErrors.length > 0;
        if (gqlErrs) {
          error.graphQLErrors.forEach((e) =>
            this.addLocalAlert({
              type: "error",
              message: e.message,
              timeout: 5,
            })
          );
        } else {
          this.addLocalAlert({
            type: "error",
            message:
              error.message ||
              "Failed to complete pre-install task. Please try again.",
            timeout: 5,
          });
        }
      } finally {
        this.completing = false;
      }
    },

    generatePreInstallUrl(qrToken) {
      const baseUrl = window.location.origin;
      const facilityId = this.task.organization.id;
      const taskId = this.task.id;
      return `${baseUrl}/manage-tasks/${taskId}/pre-install?qrToken=${encodeURIComponent(
        qrToken
      )}&facility=${facilityId}`;
    },
    titleCase,
    formatDateSlashUTC,
  },
};
</script>

<style scoped>
.task-details {
  padding-right: 16px;
}
.qr-section {
  background-color: #fafafa;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  height: fit-content;
}
.method-c-chip {
  cursor: pointer;
  font-weight: 600;
}
.method-c-chip-count {
  background: #5b8dd9;
  color: #fff;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
}
.method-c-type-select {
  max-width: 360px;
}
.method-c-placeholder {
  text-align: left;
  padding-left: 12px;
  border-left: 3px solid #e0e0e0;
}
.method-c-progress {
  font-size: 13px;
  font-weight: 600;
  padding: 6px 10px;
  background: #f5f5f5;
  border-radius: 6px;
  display: inline-block;
}
.method-c-row {
  padding: 6px 0;
}
.method-c-unit-confirmed {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #1976d2;
  border-radius: 6px;
  background: #f5f9ff;
  min-width: 120px;
}
.method-c-unit-label {
  font-size: 11px;
  color: #1976d2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.method-c-unit-value {
  font-size: 15px;
  font-weight: 700;
  color: #212121;
}
.method-c-device-tag {
  font-weight: 600;
}
.method-c-summary-title {
  text-decoration: underline;
  font-size: 18px;
  margin: 0;
}
.method-c-notif-card {
  max-width: 420px;
  margin-top: 16px;
}
</style>
